// =====================================================================
// src/modulos/usuarios.js — Usuários e perfis
//
// Três partes na mesma tela:
//   1. PESSOAS: quem entra, com papel e situação (ativo, convite pendente,
//      nunca entrou, desativado). Convidar (nome + e-mail + papel → a Edge
//      Function cria o login e o e-mail sai), editar nome/papel, desativar
//      (imediato) e reativar, reenviar convite.
//   2. PERFIS: a matriz papel × módulo. Quem edita muda os níveis e PEDE —
//      a mudança não se aplica: vai para a fila da dupla checagem
//      (solicitar_permissoes, 0002). Célula proibida (invariante) não se
//      edita e mostra o motivo.
//      0071: o CPF de cada pessoa (Editar) — vai no `cpf_solicitante` da API
//      da FNRH. A lista só vê a máscara (`cpf_dos_usuarios`); o número
//      inteiro não volta para a tela nunca.
//      0072: o Termo de Adesão — qual imobiliária (0101: o dono não tem login) já
//      aceitou a versão vigente e quem falta. Ligar e publicar é do Sistema,
//      pelo terminal (scripts/termo_adesao.js); aqui só se acompanha.
//   3. PEDIDOS: os pendentes do cliente (cancelar); para o Administrador do
//      Sistema, os de todos os clientes (aprovar / recusar com motivo).
//
// Tudo que é regra está no banco (0002/0003/0013); a tela só chama. Quem só
// VÊ o módulo enxerga as três partes sem botões.
// =====================================================================
import { el, icone, abrirModal, fecharModal, aviso, confirmar, enquantoAtualiza } from '../ui.js';
import {
  situacao, haQuanto, filtrarPessoas, agruparPorImobiliaria, agruparPorCliente, acoesPara,
  validarConvite, papeisParaConvite, validarCpf, cpfDigitos, NIVEIS, matrizDePermissoes, pedidoDeMudanca, descreverMudancas, pendentesPorPapel,
} from '../lib/usuarios.js';
import { resumoDosAceites, rotuloAderente } from '../lib/termo.js';

export function montar(cont, { auth, contexto, agora = () => Date.now() }) {
  const nivel = (contexto.modulos.find(m => m.chave === 'usuarios') || {}).nivel;
  const podeEditar = nivel === 'editar';
  const sistema = !!(contexto.usuario && contexto.usuario.sistema);
  const meuId = contexto.usuario ? contexto.usuario.id : null;
  let vivo = true;
  let pessoas = [], papeis = [], modulos = [], proibicoes = [], solicitacoes = [], clientes = [], imobiliarias = [];
  let logins = [];   // 0059: o teto de cada grupo
  let cpfs = new Map();   // 0071: usuario_id → '***.456.789-**'
  let termos = null;      // 0072: termos_situacao do hotel em foco
  let filtro = { texto: '', comInativos: false };
  let clienteSel = null;   // Sistema: o cliente em foco (null = todos)

  async function carregar() {
    const [p, pa, m, pr, s, c, a] = await Promise.all([
      auth.rpc('usuarios_do_cliente', { p_cliente: clienteSel }),
      auth.ler('papeis', 'select=id,cliente_id,chave,nome,de_fabrica,ativo,permissoes(modulo_chave,nivel)&order=nome'),
      auth.ler('modulos', 'select=chave,nome,ordem,nivel&order=ordem'),
      auth.ler('invariantes_proibicao', 'select=papel_chave,modulo_chave,motivo'),
      auth.ler('solicitacoes_permissao', 'select=id,cliente_id,papel_id,justificativa,mudancas,afetados_no_pedido,status,solicitado_em,solicitado_por,motivo&status=eq.pendente&order=solicitado_em'),
      sistema ? auth.ler('clientes', 'select=id,slug,nome,ativo&order=nome') : Promise.resolve(contexto.cliente ? [contexto.cliente] : []),
      auth.ler('imobiliarias', 'select=id,cliente_id,nome,ativo&order=nome'),
    ]);
    pessoas = p; papeis = pa; modulos = m; proibicoes = pr; solicitacoes = s; clientes = c; imobiliarias = a;
    // 0059: o contador de logins. O Sistema só vê quando escolhe um cliente.
    logins = (!sistema || clienteSel) ? (await auth.rpc('logins_em_uso', sistema ? { p_cliente: clienteSel } : {})) || [] : [];
    cpfs = new Map(((await auth.rpc('cpf_dos_usuarios', { p_cliente: clienteSel }).catch(() => [])) || []).map(c => [c.usuario_id, c.cpf_mascarado]));
    // 0072: o Sistema só vê o Termo de um hotel escolhido
    termos = (!sistema || clienteSel) ? await auth.rpc('termos_situacao', sistema ? { p_cliente: clienteSel } : {}).catch(() => null) : null;
  }
  // enquantoAtualiza: o <html> conta os ciclos, e a prova espera o contador
  // crescer em vez de adivinhar que a tela ja se refez (ui.js).
  function recarregar() {
    return enquantoAtualiza(async () => {
      try { await carregar(); } catch (e) { aviso(e.message, 'erro'); return; }
      desenhar();
    });
  }

  function desenhar() {
    if (!vivo) return;
    cont.replaceChildren();
    cont.dataset.tela = 'usuarios';
    desenharPessoas();
    desenharTermo();
    desenharPedidos();
    desenharPerfis();
  }

  const papeisDoCliente = cid => papeis.filter(p => !cid || p.cliente_id === cid);
  const clienteDe = u => sistema ? (clientes.find(c => c.id === u.cliente_id) || null) : (contexto.cliente || null);
  const nomeDoPapel = id => (papeis.find(p => p.id === id) || {}).nome || '—';
  const nomeDoCliente = id => (clientes.find(c => c.id === id) || {}).nome || '—';

  // ── 1. pessoas ─────────────────────────────────────────────────────
  function desenharPessoas() {
    const cab = el('div', { class: 'gov-cabeca' },
      el('div', { class: 'usu-filtros' },
        el('input', { type: 'search', class: 'input', id: 'usu-busca', placeholder: 'Buscar por nome, e-mail ou papel', value: filtro.texto,
          oninput: ev => { filtro.texto = ev.target.value; desenharLista(); } }),
        el('label', { style: { fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' } },
          el('input', { type: 'checkbox', id: 'usu-inativos', checked: filtro.comInativos, onchange: ev => { filtro.comInativos = ev.target.checked; desenharLista(); } }),
          'mostrar desativados'),
        sistema ? el('select', { class: 'input', id: 'usu-cliente', style: { width: 'auto' }, onchange: ev => { clienteSel = ev.target.value || null; recarregar(); } },
          el('option', { value: '' }, 'Todos os clientes'),
          clientes.map(c => el('option', { value: c.id, selected: c.id === clienteSel }, c.nome))) : null),
      podeEditar ? el('button', { type: 'button', class: 'btn btn-primary btn-sm', id: 'btn-convidar', onclick: modalConvidar }, icone('ic-plus'), 'Convidar') : null);
    cont.appendChild(cab);
    if (logins.length) cont.appendChild(barraDeLogins());
    cont.appendChild(el('div', { id: 'usu-lista' }));
    desenharLista();
  }

  // ── 0072: o Termo de Adesão ────────────────────────────────────────
  // Só aparece quando há o que mostrar: Termo publicado ou exigido. A
  // tabela é a `table.table-cards` do MdM (vira cartão no celular), e os
  // pendentes vêm primeiro (é a lista de quem cobrar).
  function desenharTermo() {
    if (!termos || (!termos.obrigatorio && !termos.versao)) return;
    const r = resumoDosAceites(termos);
    const data = termos.publicado_em ? new Date(termos.publicado_em).toLocaleDateString('pt-BR') : '';
    cont.appendChild(el('div', { class: 'usu-grupo', id: 'usu-termo', style: { marginTop: '18px' } },
      'Termo de Adesão', termos.versao ? ` — versão ${termos.versao}` + (data ? `, publicada em ${data}` : '') : ' — nenhum texto publicado'));
    cont.appendChild(el('div', { style: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', margin: '0 0 10px' } },
      el('span', { class: 'badge ' + (termos.obrigatorio ? 'badge-blue' : 'badge-gray'), id: 'usu-termo-obrigatorio' }, termos.obrigatorio ? 'Aceite obrigatório' : 'Aceite desligado'),
      el('span', { class: 'badge ' + (r.pendentes ? 'badge-orange' : 'badge-green'), id: 'usu-termo-resumo' }, `${r.emDia} de ${r.total} aceitaram`)));
    if (!termos.aderentes.length) {
      cont.appendChild(el('p', { class: 'vazio', id: 'usu-termo-vazio' }, 'Nenhuma imobiliária com login.'));
      return;
    }
    cont.appendChild(el('table', { class: 'table-cards', id: 'usu-termo-lista' },
      el('thead', null, el('tr', null, ['Aderente', 'Tipo', 'Login', 'Unidades', 'Situação', 'Documento'].map(t => el('th', null, t)))),
      el('tbody', null, termos.aderentes.map(a => el('tr', { dataset: { aderente: a.usuario_id, emDia: String(!!a.em_dia) } },
        el('td', { 'data-label': 'Aderente' }, el('span', { class: 'usu-nome' }, a.aderente || '')),
        el('td', { 'data-label': 'Tipo' }, rotuloAderente(a.tipo)),
        el('td', { 'data-label': 'Login' }, a.usuario || ''),
        el('td', { 'data-label': 'Unidades' }, String(a.unidades || 0)),
        el('td', { 'data-label': 'Situação' }, a.em_dia
          ? el('span', { class: 'badge badge-green' }, `aceito em ${new Date(a.aceito_em).toLocaleDateString('pt-BR')}`)
          : el('span', { class: 'badge badge-orange' }, a.aceito_em ? `pendente (aceitou a versão ${a.versao_aceita})` : 'pendente')),
        el('td', { 'data-label': 'Documento' }, a.documento || '—'))))));
  }

  // ── 0059: o teto de logins ─────────────────────────────────────────
  // Hugo, 24/09: 80 do hotel, 1 por imobiliária (ou proprietário com
  // administração própria), adicionais a R$ 130,00/mês. Quem trava é o
  // banco; aqui a pessoa vê quanto usa, e o Sistema ajusta os adicionais.
  function barraDeLogins() {
    return el('div', { id: 'usu-logins', class: 'usu-grupo', style: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', margin: '0 0 12px' } },
      el('span', { style: { fontSize: '12px', color: '#6b7280', fontWeight: '600' } }, 'Logins em uso:'),
      logins.map(g => {
        const cheio = g.usados >= g.teto;
        return el('span', { class: 'badge ' + (cheio ? 'badge-orange' : 'badge-gray'), dataset: { grupo: g.grupo },
            title: `${g.incluidos} incluído(s)` + (g.adicionais ? ` + ${g.adicionais} adicional(is)` : '') },
          `${g.nome}: ${g.usados} de ${g.teto}`,
          sistema ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', dataset: { adicionais: g.grupo }, style: { marginLeft: '6px', padding: '0 6px' },
            onclick: () => modalAdicionais(g) }, '±') : null);
      }));
  }
  function modalAdicionais(g) {
    const campo = el('input', { class: 'input', id: 'usu-adicionais', type: 'number', min: '0', value: String(g.adicionais) });
    abrirModal([
      el('h2', { class: 'modal-title' }, `Logins adicionais — ${g.nome}`),
      el('p', { class: 'usu-email', style: { margin: '0 0 10px' } }, `${g.incluidos} incluído(s) na mensalidade; cada adicional é cobrado à parte (R$ 130,00/mês na proposta).`),
      el('div', { class: 'form-row' }, campo),
      el('div', { style: { display: 'flex', gap: '10px' } },
        el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Cancelar'),
        el('button', { type: 'button', class: 'btn btn-primary', id: 'btn-salvar-adicionais', style: { flex: '1' }, onclick: async () => {
          try { await auth.rpc('definir_logins_adicionais', { p_cliente: clienteSel, p_imobiliaria: g.grupo === 'hotel' ? null : g.grupo, p_quantidade: Number(campo.value) }); }
          catch (e) { aviso(e.message, 'erro'); return; }
          fecharModal(); recarregar(); aviso('Logins adicionais atualizados.');
        } }, 'Salvar')),
    ]);
  }

  function desenharLista() {
    const alvo = cont.querySelector('#usu-lista'); if (!alvo) return;
    alvo.replaceChildren();
    const lista = filtrarPessoas(pessoas, filtro);
    if (!lista.length) { alvo.appendChild(el('p', { class: 'vazio', id: 'usu-vazio' }, pessoas.length ? 'Ninguém com esse filtro.' : 'Ninguém cadastrado ainda. Convide a primeira pessoa.')); return; }
    const grupos = sistema && !clienteSel ? agruparPorCliente(lista, clientes) : agruparPorImobiliaria(lista);
    for (const g of grupos) {
      if (grupos.length > 1 || sistema) alvo.appendChild(el('div', { class: 'usu-grupo' }, g.nome));
      const tabela = el('table', { class: 'table-cards' },
        el('thead', null, el('tr', null, el('th', null, 'Pessoa'), el('th', null, 'Papel'), el('th', null, 'Situação'), el('th', null, 'Último acesso'), el('th', null, ''))),
        el('tbody', null, g.pessoas.map(linhaPessoa)));
      alvo.appendChild(tabela);
    }
  }

  function linhaPessoa(u) {
    const s = situacao(u);
    const acoes = acoesPara(u, { podeEditar, meuId });
    const botoes = [];
    if (acoes.includes('editar') || acoes.includes('renomear')) botoes.push(el('button', { type: 'button', class: 'btn btn-secondary btn-sm', 'aria-label': 'Editar', title: 'Editar', onclick: () => modalEditar(u) }, icone('ic-edit')));
    if (acoes.includes('reenviar')) botoes.push(el('button', { type: 'button', class: 'btn btn-secondary btn-sm', title: 'Reenviar convite', onclick: () => reenviar(u) }, 'Reenviar convite'));
    if (acoes.includes('desativar')) botoes.push(el('button', { type: 'button', class: 'btn btn-contorno-vermelho btn-sm', title: 'Desativar', onclick: () => desativar(u) }, 'Desativar'));
    if (acoes.includes('reativar')) botoes.push(el('button', { type: 'button', class: 'btn btn-verde btn-sm', title: 'Reativar', onclick: () => reativar(u) }, 'Reativar'));
    return el('tr', { dataset: { usuario: u.id, situacao: s.chave } },
      el('td', null, el('div', { class: 'usu-nome' }, u.nome, u.id === meuId ? ' (você)' : ''), el('div', { class: 'usu-email' }, u.email || ''),
        cpfs.has(u.id) ? el('div', { class: 'usu-email', dataset: { cpf: u.id } }, `CPF ${cpfs.get(u.id)}`) : null),
      el('td', null, u.papel_nome || '—', u.imobiliaria_nome ? el('div', { class: 'usu-email' }, u.imobiliaria_nome) : null),
      el('td', null, el('span', { class: 'badge ' + s.classe }, s.rotulo)),
      el('td', null, u.ultimo_acesso ? haQuanto(u.ultimo_acesso, agora()) : '—'),
      el('td', null, el('div', { class: 'usu-acoes' }, botoes)));
  }

  // 0091 (2A): de imobiliária, só o papel de imobiliária
  const opcoesDePapel = (cid, atual, imobiliaria) => [el('option', { value: '' }, '— escolha —'),
    ...papeisParaConvite(papeisDoCliente(cid), { imobiliaria }).map(p => el('option', { value: p.id, selected: p.id === atual }, p.nome))];
  function seletorPapel(id, cid, atual, imobiliaria) {
    return el('select', { class: 'input', id }, ...opcoesDePapel(cid, atual, imobiliaria));
  }
  function seletorImobiliaria(id, cid, atual) {
    const lista = imobiliarias.filter(a => a.cliente_id === cid && a.ativo);
    if (!lista.length) return null;
    return el('div', { class: 'form-row' }, el('label', { class: 'label', for: id }, 'Imobiliária (só se a pessoa for de uma)'),
      el('select', { class: 'input', id }, el('option', { value: '' }, '— equipe do prédio —'),
        lista.map(a => el('option', { value: a.id, selected: a.id === atual }, a.nome))));
  }
  function erroModal(msg) { const e = document.getElementById('modal-erro'); if (e) { e.textContent = msg || ''; e.classList.toggle('hidden', !msg); } }

  function modalConvidar() {
    const cid = sistema ? (clienteSel || (clientes[0] && clientes[0].id)) : contexto.cliente.id;
    if (!cid) { aviso('Nenhum cliente para convidar.', 'erro'); return; }
    const selCliente = sistema ? el('select', { class: 'input', id: 'conv-cliente', onchange: ev => { const c = ev.target.value; const adm = seletorImobiliaria('conv-adm', c); admWrap.replaceChildren(adm || ''); refazerPapeis(); } },
      clientes.map(c => el('option', { value: c.id, selected: c.id === cid }, c.nome))) : null;
    const minhaAdm = contexto.imobiliaria ? contexto.imobiliaria.id : null;
    const papel = seletorPapel('conv-papel', cid, null, !!minhaAdm);
    const admWrap = el('div', null, seletorImobiliaria('conv-adm', cid, minhaAdm));
    // 0091 (2A): escolheu imobiliária, o papel se restringe ao dela
    const clienteDoConvite = () => (selCliente ? selCliente.value : cid);
    const admEscolhida = () => !!((admWrap.querySelector('#conv-adm') || {}).value || minhaAdm);
    function refazerPapeis() { papel.replaceChildren(...opcoesDePapel(clienteDoConvite(), papel.value, admEscolhida())); }
    admWrap.addEventListener('change', ev => { if (ev.target && ev.target.id === 'conv-adm') refazerPapeis(); });
    const form = el('form', { id: 'form-convidar', onsubmit: async ev => {
      ev.preventDefault();
      const dados = { nome: form.querySelector('#conv-nome').value, email: form.querySelector('#conv-email').value, papel_id: papel.value || null,
        imobiliaria_id: (form.querySelector('#conv-adm') || {}).value || null, cliente_id: sistema ? selCliente.value : null };
      const erros = validarConvite(dados);
      if (erros.length) { erroModal(erros.join(' · ')); return; }
      const btn = form.querySelector('#btn-conv-enviar'); btn.disabled = true; erroModal('');
      try { await auth.funcao('convidar_usuario', { acao: 'convidar', ...dados }); }
      catch (e) { erroModal(e.message); btn.disabled = false; return; }
      fecharModal(); aviso(`Convite enviado para ${dados.email.trim()}.`); recarregar();
    } },
      el('h2', { class: 'modal-title' }, 'Convidar pessoa'),
      el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' }),
      selCliente ? el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'conv-cliente' }, 'Cliente'), selCliente) : null,
      el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'conv-nome' }, 'Nome'), el('input', { class: 'input', id: 'conv-nome', autocomplete: 'off' })),
      el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'conv-email' }, 'E-mail'), el('input', { class: 'input', id: 'conv-email', type: 'text', inputmode: 'email', autocomplete: 'off', autocapitalize: 'none' })),
      el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'conv-papel' }, 'Papel'), papel),
      admWrap,
      el('p', { style: { fontSize: '13px', color: '#6b7280', margin: '0 0 14px' } }, 'A pessoa recebe um e-mail com o link para definir a senha. O acesso é o do papel escolhido — e pode ser desativado a qualquer momento.'),
      el('div', { style: { display: 'flex', gap: '10px' } },
        el('button', { type: 'submit', class: 'btn btn-primary', id: 'btn-conv-enviar', style: { flex: '1' } }, 'Enviar convite'),
        el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Cancelar')));
    abrirModal(form);
    form.querySelector('#conv-nome').focus();
  }

  function modalEditar(u) {
    const souEu = u.id === meuId;
    const c = clienteDe(u);
    const papel = seletorPapel('ed-papel', c ? c.id : null, u.papel_id, !!u.imobiliaria_id);   // 0091 (2A)
    if (souEu) papel.disabled = true;
    // 0071: o CPF — para o envio à FNRH (quem faz check-in precisa ter)
    const atual = cpfs.get(u.id);
    let removerCpf = false;
    const dicaCpf = el('div', { class: 'usu-email', id: 'ed-cpf-dica', style: { marginTop: '4px' } },
      atual ? `Cadastrado: ${atual}. Em branco, fica o mesmo.` : 'Obrigatório para quem faz check-in quando o envio à FNRH está ligado.');
    const inputCpf = el('input', { class: 'input', id: 'ed-cpf', inputmode: 'numeric', autocomplete: 'off', placeholder: '000.000.000-00', maxlength: '14',
      oninput: () => { removerCpf = false; } });
    const campoCpf = el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'ed-cpf' }, 'CPF'), inputCpf, dicaCpf,
      atual ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'ed-cpf-remover', style: { marginTop: '6px' },
        onclick: ev => { removerCpf = true; inputCpf.value = ''; dicaCpf.textContent = 'O CPF será removido ao salvar.'; ev.target.disabled = true; } }, 'Remover CPF') : null);
    const form = el('form', { id: 'form-editar', onsubmit: async ev => {
      ev.preventDefault();
      const nome = form.querySelector('#ed-nome').value;
      if (!nome.trim()) { erroModal('Nome é obrigatório'); return; }
      // 0071: CPF em branco = mantém o que está; "Remover" apaga
      const cpf = form.querySelector('#ed-cpf').value;
      const erroCpf = validarCpf(cpf);
      if (erroCpf) { erroModal(erroCpf); return; }
      try {
        await auth.rpc('alterar_usuario', { p_usuario: u.id, p_nome: nome, p_papel_id: papel.value || u.papel_id });
        if (removerCpf) await auth.rpc('definir_cpf_usuario', { p_usuario: u.id, p_cpf: null });
        else if (cpfDigitos(cpf)) await auth.rpc('definir_cpf_usuario', { p_usuario: u.id, p_cpf: cpfDigitos(cpf) });
      } catch (e) { erroModal(e.message); return; }
      fecharModal(); aviso('Salvo.'); recarregar();
    } },
      el('h2', { class: 'modal-title' }, souEu ? 'Meu nome' : 'Editar pessoa'),
      el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' }),
      el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'ed-nome' }, 'Nome'), el('input', { class: 'input', id: 'ed-nome', value: u.nome, required: true })),
      el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'ed-papel' }, 'Papel'), papel,
        souEu ? el('div', { class: 'usu-email', style: { marginTop: '4px' } }, 'O próprio papel não se troca — peça a outra pessoa da administração.') : null),
      el('div', { class: 'usu-email', style: { marginBottom: '14px' } }, u.email || ''),
      campoCpf,
      el('div', { style: { display: 'flex', gap: '10px' } },
        el('button', { type: 'submit', class: 'btn btn-primary', id: 'btn-ed-salvar', style: { flex: '1' } }, 'Salvar'),
        el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Cancelar')));
    abrirModal(form);
  }

  async function desativar(u) {
    const sim = await confirmar({ titulo: `Desativar ${u.nome}?`, texto: 'O acesso fecha na hora, inclusive na sessão que a pessoa tiver aberta. Dá para reativar depois.', sim: 'Desativar', perigoso: true });
    if (!sim) return;
    try { await auth.rpc('desativar_usuario', { p_usuario: u.id }); } catch (e) { aviso(e.message, 'erro'); return; }
    aviso(`${u.nome} desativado.`); recarregar();
  }
  async function reativar(u) {
    try { await auth.rpc('reativar_usuario', { p_usuario: u.id }); } catch (e) { aviso(e.message, 'erro'); return; }
    aviso(`${u.nome} reativado.`); recarregar();
  }
  async function reenviar(u) {
    try { await auth.funcao('convidar_usuario', { acao: 'reenviar', email: u.email }); } catch (e) { aviso(e.message, 'erro'); return; }
    aviso(`Convite reenviado para ${u.email}.`);
  }

  // ── 2. pedidos pendentes ───────────────────────────────────────────
  function desenharPedidos() {
    const lista = solicitacoes.filter(s => !clienteSel || s.cliente_id === clienteSel);
    if (!lista.length) return;
    cont.appendChild(el('div', { class: 'secao-titulo' }, sistema ? 'Pedidos de mudança de perfil para decidir' : 'Pedidos de mudança de perfil aguardando o Administrador do Sistema'));
    const box = el('div', { id: 'usu-pedidos' });
    for (const s of lista) {
      box.appendChild(el('div', { class: 'solicitacao', dataset: { solicitacao: s.id } },
        el('div', { style: { fontWeight: '600' } }, (sistema ? nomeDoCliente(s.cliente_id) + ' · ' : '') + nomeDoPapel(s.papel_id),
          el('span', { class: 'usu-email', style: { marginLeft: '8px' } }, `${s.afetados_no_pedido} pessoa(s) afetada(s) · pedido em ${new Date(s.solicitado_em).toLocaleDateString('pt-BR')}`)),
        el('ul', null, descreverMudancas(s.mudancas, modulos).map(t => el('li', null, t))),
        s.justificativa ? el('div', { class: 'usu-email', style: { marginBottom: '8px' } }, 'Justificativa: ' + s.justificativa) : null,
        podeEditar ? el('div', { style: { display: 'flex', gap: '8px', flexWrap: 'wrap' } },
          sistema ? el('button', { type: 'button', class: 'btn btn-verde btn-sm', onclick: () => aprovar(s) }, 'Aprovar') : null,
          sistema ? el('button', { type: 'button', class: 'btn btn-contorno-vermelho btn-sm', onclick: () => recusar(s) }, 'Recusar') : null,
          !sistema ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', onclick: () => cancelar(s) }, 'Cancelar pedido') : null) : null));
    }
    cont.appendChild(box);
  }
  async function aprovar(s) {
    const sim = await confirmar({ titulo: 'Aprovar esta mudança?', texto: 'Ela passa a valer na hora para todas as pessoas com esse papel.', sim: 'Aprovar' });
    if (!sim) return;
    try { await auth.rpc('aprovar_solicitacao', { p_id: s.id }); } catch (e) { aviso(e.message, 'erro'); return; }
    aviso('Aprovada.'); recarregar();
  }
  function recusar(s) {
    const form = el('form', { id: 'form-recusar', onsubmit: async ev => {
      ev.preventDefault();
      const motivo = form.querySelector('#rec-motivo').value;
      try { await auth.rpc('recusar_solicitacao', { p_id: s.id, p_motivo: motivo }); } catch (e) { erroModal(e.message); return; }
      fecharModal(); aviso('Recusada.'); recarregar();
    } },
      el('h2', { class: 'modal-title' }, 'Recusar o pedido'),
      el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' }),
      el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'rec-motivo' }, 'Motivo (quem pediu vai ler)'), el('textarea', { class: 'input', id: 'rec-motivo', rows: 3, required: true })),
      el('div', { style: { display: 'flex', gap: '10px' } },
        el('button', { type: 'submit', class: 'btn btn-vermelho', style: { flex: '1' } }, 'Recusar'),
        el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Voltar')));
    abrirModal(form);
  }
  async function cancelar(s) {
    try { await auth.rpc('cancelar_solicitacao', { p_id: s.id }); } catch (e) { aviso(e.message, 'erro'); return; }
    aviso('Pedido cancelado.'); recarregar();
  }

  // ── 3. perfis ──────────────────────────────────────────────────────
  function desenharPerfis() {
    const cid = sistema ? clienteSel : (contexto.cliente ? contexto.cliente.id : null);
    if (sistema && !cid) { cont.appendChild(el('div', { class: 'secao-titulo' }, 'Perfis')); cont.appendChild(el('p', { class: 'vazio' }, 'Escolha um cliente acima para ver os perfis.')); return; }
    const linhas = matrizDePermissoes(papeisDoCliente(cid), modulos, proibicoes);
    const pend = pendentesPorPapel(solicitacoes);
    cont.appendChild(el('div', { class: 'secao-titulo' }, 'Perfis — o que cada papel alcança'));
    cont.appendChild(el('p', { class: 'usu-email', style: { margin: '0 0 10px' } },
      podeEditar && !sistema ? 'Mude os níveis e clique em "Pedir mudança": a alteração só vale depois que o Administrador do Sistema aprovar. Célula vermelha é invariante do produto — ninguém muda.'
        : 'Célula vermelha é invariante do produto — ninguém muda.'));
    const mods = linhas.length ? linhas[0].celulas : [];
    const novos = new Map();   // papel_id → {modulo: nivel}
    const tabela = el('table', null,
      el('thead', null, el('tr', null, el('th', null, 'Papel'), mods.map(m => el('th', { class: 'mod', title: m.modulo_nome }, m.modulo_nome)), el('th', null, ''))),
      el('tbody', null, linhas.map(l => {
        const mudados = {};
        novos.set(l.papel_id, mudados);
        const btnPedir = el('button', { type: 'button', class: 'btn btn-primary btn-sm', disabled: true, dataset: { pedir: l.chave }, onclick: () => modalPedir(l, pedidoDeMudanca(l, mudados)) }, 'Pedir mudança');
        const tr = el('tr', { dataset: { papel: l.chave } },
          el('td', null, el('div', { class: 'usu-nome' }, l.nome), l.de_fabrica ? el('div', { class: 'usu-email' }, 'de fábrica') : null,
            pend.get(l.papel_id) ? el('span', { class: 'badge badge-yellow' }, 'pedido pendente') : null),
          l.celulas.map(c => {
            if (c.proibido) return el('td', { class: 'cel proibido', title: c.proibido }, '✕');
            if (!podeEditar || sistema) return el('td', { class: 'cel' }, el('span', { class: 'nivel-' + c.nivel }, c.nivel === 'nenhum' ? '·' : c.nivel));
            const sel = el('select', { dataset: { modulo: c.modulo }, onchange: ev => {
              const v = ev.target.value;
              if (v === c.nivel) delete mudados[c.modulo]; else mudados[c.modulo] = v;
              ev.target.parentElement.classList.toggle('mudou', v !== c.nivel);
              btnPedir.disabled = Object.keys(pedidoDeMudanca(l, mudados)).length === 0;
            } }, NIVEIS.map(n => el('option', { value: n, selected: n === c.nivel }, n === 'nenhum' ? '—' : n)));
            return el('td', { class: 'cel' }, sel);
          }),
          el('td', null, podeEditar && !sistema ? btnPedir : null));
        return tr;
      })));
    cont.appendChild(el('div', { class: 'matriz card', style: { padding: '8px' } }, tabela));
  }

  function modalPedir(linha, niveis) {
    const mudancas = Object.entries(niveis).map(([modulo, para]) => ({ modulo, de: (linha.celulas.find(c => c.modulo === modulo) || {}).nivel, para }));
    const form = el('form', { id: 'form-pedir', onsubmit: async ev => {
      ev.preventDefault();
      try { await auth.rpc('solicitar_permissoes', { p_papel_id: linha.papel_id, p_niveis: niveis, p_justificativa: form.querySelector('#ped-just').value || null }); }
      catch (e) { erroModal(e.message); return; }
      fecharModal(); aviso('Pedido enviado ao Administrador do Sistema.'); recarregar();
    } },
      el('h2', { class: 'modal-title' }, `Pedir mudança: ${linha.nome}`),
      el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' }),
      el('ul', { style: { margin: '0 0 14px 18px', fontSize: '14px' } }, descreverMudancas(mudancas, modulos).map(t => el('li', null, t))),
      el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'ped-just' }, 'Justificativa (opcional)'), el('textarea', { class: 'input', id: 'ped-just', rows: 2 })),
      el('div', { style: { display: 'flex', gap: '10px' } },
        el('button', { type: 'submit', class: 'btn btn-primary', id: 'btn-ped-enviar', style: { flex: '1' } }, 'Enviar pedido'),
        el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Cancelar')));
    abrirModal(form);
  }

  recarregar();
  return () => { vivo = false; };
}
